import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Sparkles, GripVertical, Lock, Trash2, Plus, ExternalLink, Eye, Copy, Search,
  User, Mail, Phone, FileText, Link2, Globe2, Briefcase, DollarSign, MessageSquare, Puzzle,
  Calendar as CalendarIcon, Hash, AlignLeft, ToggleLeft, List, Type, MapPin, Linkedin, Users, Building2,
  ListChecks,
  AudioLines, Clapperboard,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CurrencySelect } from '@/components/ui/currency-select'
import { DatePickerVirgilio } from '@/components/ui/date-picker-virgilio'
import { SectionCard, FieldLabel, FieldHint, ToggleRow, AiAssistedBadge, SalaryInput, ChipInput, currencySymbol, periodSuffix } from './_parts'
import type { CreateJobData } from '@/hooks/useJobs'
import { useJobPostings } from '@/hooks/useJobPostings'
import { useApplicationFields } from '@/hooks/useApplicationFields'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { CharCounterLine } from '@/components/ui/char-counter'
import { AffixInput } from '@/components/ui/affix-input'
import { useJobsWithPostings } from '@/hooks/useJobsWithPostings'
import { useTenant } from '@/hooks/useTenant'
import { ApplicationFormBuilder, SMART_FIELD_TYPES_SET as SMART_FIELD_TYPES_SHARED, newOptionValue, type AppField as SharedAppField, type ChoiceOption, type FieldType as SharedFieldType, iconForType as iconForTypeShared } from '@/components/jobs/postings/ApplicationFormBuilder'
import { PostingChannelsCard, type ChannelsValue } from '@/components/jobs/postings/PostingChannelsCard'
import { confirmDialog } from '@/components/ui/confirm-dialog'
import { FormattedTextBox } from '@/components/ui/formatted-text'
import { markdownToHtml } from '@/utils/markdown'
import { PostingVideoField } from '@/components/jobs/postings/PostingVideoField'
import { NO_VIDEO, brandingDetails, mediaFileProblem, uploadPostingMedia, type PostingVideo } from '@/lib/postingMedia'
import { finishSlug, typedSlug } from '@/lib/postingUrl'
import { shakeFirstInvalid } from '@/lib/motion'


/* ---------------- helpers ---------------- */
function slugify(s: string) {
  return s.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const LANGUAGE_OPTIONS = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-ES', label: 'Spanish' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
  { value: 'pt-BR', label: 'Portuguese (BR)' },
  { value: 'it-IT', label: 'Italian' },
]

const BRAND_SWATCHES = ['#6F3FF5', '#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#0d0d09']

type FieldType =
  | 'text' | 'email' | 'phone' | 'file' | 'url' | 'yesno' | 'select' | 'multiselect' | 'number' | 'longtext' | 'date'
  | 'salary' | 'location' | 'linkedin' | 'recruiter' | 'employment_type' | 'work_location' | 'score'
  | 'audio' | 'video'

interface SmartFieldDef {
  id: string
  label: string
  type: FieldType
  icon: React.ComponentType<{ className?: string }>
  hint: string
  description: string
}

const SMART_FIELDS: SmartFieldDef[] = [
  { id: 'sf_salary',          label: 'Salary expectations', type: 'salary',          icon: DollarSign, hint: 'Currency-aware',                    description: 'Expected compensation' },
  { id: 'sf_location',        label: 'Location',            type: 'location',        icon: MapPin,     hint: 'City · state · country',            description: 'Where the candidate is based' },
  { id: 'sf_phone',           label: 'Phone',               type: 'phone',           icon: Phone,      hint: 'International format',              description: 'Contact phone number' },
  { id: 'sf_linkedin',        label: 'LinkedIn',            type: 'linkedin',        icon: Linkedin,   hint: 'Profile URL',                       description: 'LinkedIn profile' },
  { id: 'sf_employment_type', label: 'Employment type',     type: 'employment_type', icon: Briefcase,  hint: 'Full-time · part-time · contract',  description: 'Preferred employment type' },
  { id: 'sf_work_location',   label: 'Work location',       type: 'work_location',   icon: Building2,  hint: 'Remote · hybrid · on-site',         description: 'Preferred work arrangement' },
  { id: 'sf_recruiter',       label: 'Preferred recruiter', type: 'recruiter',       icon: Users,      hint: 'Team member assignment',            description: 'Routes the application to a recruiter' },
]

const BASIC_TYPES: { type: FieldType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { type: 'text',     label: 'Short text',    icon: Type },
  { type: 'longtext', label: 'Long text',     icon: AlignLeft },
  { type: 'number',   label: 'Number',        icon: Hash },
  { type: 'email',    label: 'Email',         icon: Mail },
  { type: 'url',      label: 'URL',           icon: Link2 },
  { type: 'date',     label: 'Date',          icon: CalendarIcon },
  { type: 'select',   label: 'Single select', icon: List },
  { type: 'yesno',    label: 'Yes / No',      icon: ToggleLeft },
  { type: 'file',     label: 'File upload',   icon: FileText },
]


interface AppField {
  id: string
  label: string
  type: FieldType
  hint?: string
  required: boolean
  locked?: boolean
  isSmart?: boolean
  icon: React.ComponentType<{ className?: string }>
  options?: ChoiceOption[]
}

const SMART_FIELD_TYPES = new Set<FieldType>(SMART_FIELDS.map((s) => s.type))

const TYPE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  text: Type, longtext: AlignLeft, number: Hash, email: Mail, url: Link2,
  date: CalendarIcon, select: List, multiselect: ListChecks, yesno: ToggleLeft, file: FileText, phone: Phone,
  linkedin: Linkedin, location: MapPin, salary: DollarSign, employment_type: Briefcase,
  work_location: Building2, recruiter: Users, audio: AudioLines, video: Clapperboard,
}
const iconForType = (t: string) => TYPE_ICON[t] || MessageSquare

/** Map the wizard's local AppField type → the DB field_type enum used in job_posting_application_fields. */
function sharedTypeToDbType(t: FieldType): string {
  if (t === 'longtext') return 'textarea'
  if (t === 'yesno') return 'checkbox'
  if (t === 'multiselect') return 'checkbox_group'
  return t
}

const NOTICE_PERIOD_OPTIONS: ChoiceOption[] = ['Immediately', '1 week', '2 weeks', '1 month', '2 months', '3+ months']
  .map((label) => ({ label, value: newOptionValue(label) }))

/** Generate a snake_case field_name suitable for the application_fields contract. */
function sanitizeFieldName(label: string, index: number): string {
  const base = (label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return base ? `${base}_${index + 1}` : `question_${index + 1}`
}

const DEFAULT_FIELDS: AppField[] = [
  { id: 'full_name', label: 'Full name', type: 'text', hint: 'Short text', required: true, locked: true, icon: User },
  { id: 'email', label: 'Email', type: 'email', hint: 'Email', required: true, locked: true, icon: Mail },
  { id: 'phone', label: 'Phone', type: 'phone', hint: 'Phone', required: true, locked: true, icon: Phone },
  { id: 'resume', label: 'Resume / CV', type: 'file', hint: 'File upload · PDF, DOC', required: true, locked: true, icon: FileText },
  { id: 'linkedin', label: 'LinkedIn Profile', type: 'linkedin', hint: 'Profile URL · syncs to candidate', required: true, locked: true, isSmart: true, icon: Linkedin },
  { id: 'work_auth', label: 'Are you authorized to work in the US?', type: 'yesno', hint: 'Yes / No', required: true, icon: Globe2 },
  { id: 'notice', label: 'Notice period', type: 'select', required: false, icon: Briefcase, options: NOTICE_PERIOD_OPTIONS },
  { id: 'salary', label: 'Salary expectations (optional)', type: 'number', hint: 'Number · currency-aware', required: false, icon: DollarSign },
  { id: 'why', label: 'Why are you interested in this role?', type: 'longtext', hint: 'Long text · 600 chars', required: true, icon: MessageSquare },
]

interface Channel {
  id: string
  letter: string
  letterBg: string
  name: string
  sub: string
  status: string
  defaultOn: boolean
  alwaysOn?: boolean
  cost?: number
}

const CHANNELS: Channel[] = [
  { id: 'careers', letter: 'A', letterBg: 'bg-[#0d0d09] text-white', name: 'Acme Talent careers page', sub: 'Your public careers page · public listing', status: 'Always on', defaultOn: true, alwaysOn: true, cost: 0 },
  { id: 'linkedin', letter: 'in', letterBg: 'bg-[#0A66C2] text-white', name: 'LinkedIn', sub: 'Slots used: 7 / 10 · auto-renew weekly', status: 'Connected · ~$0 (free slot)', defaultOn: true, cost: 0 },
  { id: 'wttj', letter: 'W', letterBg: 'bg-[#FFE74C] text-[#0d0d09]', name: 'Welcome to the Jungle', sub: 'Cross-post · Europe + remote-friendly audience', status: 'Connected · $0', defaultOn: true, cost: 0 },
  { id: 'zip', letter: 'Z', letterBg: 'bg-[#3B82F6] text-white', name: 'ZipRecruiter', sub: 'Paid sponsored placement · 30-day campaign', status: '$129 / 30 days', defaultOn: true, cost: 129 },
  { id: 'google', letter: 'G', letterBg: 'bg-white text-text-primary border border-virgilio-border', name: 'Google for Jobs', sub: 'Structured data feed · auto-syndicated', status: 'Free · auto', defaultOn: false, cost: 0 },
  { id: 'indeed', letter: 'I', letterBg: 'bg-[#003A9B] text-white', name: 'Indeed (free tier)', sub: 'Organic listing only — sponsorship requires upgrade', status: 'Not connected', defaultOn: false, cost: 0 },
]

/* ---------------- component ---------------- */
interface JobPostingStepProps {
  jobData: Partial<CreateJobData>
  onUpdate: (data: Partial<CreateJobData>) => void
  jobId: string | null
  onPostingMeta?: (meta: { channels: number; fields: number }) => void
}

export interface JobPostingStepHandle {
  /** Persist (or skip persist) and return true to allow advancing.
   *  When a posting is created, the new posting id is also returned. */
  savePosting: (opts?: { publish?: boolean }) => Promise<{ ok: boolean; postingId?: string | null }>
}

export const JobPostingStep = React.forwardRef<JobPostingStepHandle, JobPostingStepProps>(
  function JobPostingStep({ jobData, onUpdate, jobId, onPostingMeta }, ref) {
    const { createPosting, updatePosting } = useJobPostings(jobId || '')
    const { fields: smartFieldsLibrary } = useApplicationFields()
    const { tenant } = useTenant()
    const careersPageName = tenant?.name ? `${tenant.name}'s careers page` : 'Your careers page'

    const setJob = <K extends keyof CreateJobData>(field: K, value: CreateJobData[K]) =>
      onUpdate({ [field]: value } as Partial<CreateJobData>)

    /* --- basics --- */
    const [publicTitle, setPublicTitle] = useState(jobData.title || '')
    const [slug, setSlug] = useState(slugify(jobData.title || ''))
    const [refId, setRefId] = useState('')
    const [language, setLanguage] = useState('en-US')
    const [deadline, setDeadline] = useState<Date | undefined>(undefined)
    const [showInSearch, setShowInSearch] = useState(true)
    const [showResponseBadge, setShowResponseBadge] = useState(true)

    /* --- posting locations (seeded from the parent job) --- */
    const [primaryLocation, setPrimaryLocation] = useState(jobData.location || '')
    const [additionalLocations, setAdditionalLocations] = useState<string[]>(jobData.additional_locations || [])

    /* --- compensation (commissions only — base salary lives on jobData) --- */
    const [variableEnabled, setVariableEnabled] = useState(false)
    const [commissionCurrency, setCommissionCurrency] = useState(jobData.currency || 'USD')
    const [commissionAmount, setCommissionAmount] = useState<number | undefined>(undefined)

    const salaryInvalid =
      jobData.salary_min != null &&
      jobData.salary_max != null &&
      jobData.salary_min > jobData.salary_max


    // keep slug in sync with title until user manually edits it
    const slugTouched = useRef(false)
    const [slugError, setSlugError] = useState<string | null>(null)
    useEffect(() => {
      if (!slugTouched.current) setSlug(slugify(publicTitle))
    }, [publicTitle])

    /* --- description --- */
    const [description, setDescription] = useState('')
    const initedDesc = useRef(false)
    useEffect(() => {
      if (!initedDesc.current && jobData.description) {
        setDescription(markdownToHtml(jobData.description))
        initedDesc.current = true
      }
    }, [jobData.description])
    const [genLoading, setGenLoading] = useState(false)

    const handleGenerate = async () => {
      if (!jobData.title) {
        toast.error('Please fill in more details to generate a description')
        return
      }
      if (description.trim() && !(await confirmDialog({ title: 'Replace the current description?', description: 'Gio will write a fresh one from the job details.', confirmLabel: 'Replace' }))) return
      setGenLoading(true)
      try {
        const { data, error } = await supabase.functions.invoke('generate-job-description', {
          body: { jobData },
        })
        if (error) {
          let msg = error.message
          try {
            const body = await (error as any).context?.json?.()
            if (body?.message) msg = body.message
          } catch { /* keep default */ }
          throw new Error(msg)
        }
        if (data?.description) {
          setDescription(markdownToHtml(data.description))
          toast.success('Description generated')
        }
      } catch (e: any) {
        toast.error(e?.message || 'Failed to generate description')
      } finally {
        setGenLoading(false)
      }
    }

    /* --- branding --- */
    const [brandColor, setBrandColor] = useState('#6F3FF5')
    const [bannerName, setBannerName] = useState('')
    const [bannerUrl, setBannerUrl] = useState<string | null>(null)
    const [bannerUploading, setBannerUploading] = useState(false)
    const [video, setVideo] = useState<PostingVideo>(NO_VIDEO)

    async function handleBannerFileChange(file: File | null) {
      if (!file) return
      const problem = mediaFileProblem(file, 'cover')
      if (problem) {
        toast.error(problem)
        return
      }
      if (!jobId) {
        toast.error('Save the job first, then upload its cover.')
        return
      }
      setBannerUploading(true)
      try {
        // Stored with the posting's other media, so the public job post shows it.
        setBannerUrl(await uploadPostingMedia(jobId, file, 'cover'))
        setBannerName(file.name)
        toast.success('Hero banner uploaded.')
      } catch (e) {
        toast.error((e as Error).message)
      } finally {
        setBannerUploading(false)
      }
    }

    /* --- application form --- */
    // Pay period of the public salary range, saved on the posting.
    const [salaryPeriod, setSalaryPeriod] = useState('annually')
    const [fields, setFields] = useState<AppField[]>(DEFAULT_FIELDS)
    const [eeo, setEeo] = useState(true)
    const dragIdx = useRef<number | null>(null)
    const onDragStart = (i: number) => () => { dragIdx.current = i }
    const onDragOver = (i: number) => (e: React.DragEvent) => {
      e.preventDefault()
      const from = dragIdx.current
      if (from == null || from === i) return
      if (fields[i].locked || fields[from].locked) return
      const next = [...fields]
      const [moved] = next.splice(from, 1)
      next.splice(i, 0, moved)
      dragIdx.current = i
      setFields(next)
    }

    /* --- channels --- */
    const [channelOn, setChannelOn] = useState<Record<string, boolean>>(
      Object.fromEntries(CHANNELS.map((c) => [c.id, c.defaultOn]))
    )
    const enabledChannels = CHANNELS.filter((c) => channelOn[c.id])
    const totalCost = enabledChannels.reduce((s, c) => s + (c.cost || 0), 0)
    const paidCount = enabledChannels.filter((c) => (c.cost || 0) > 0).length
    const freeCount = enabledChannels.length - paidCount

    /* --- real channels (wired to DB fields) --- */
    const [postingChannels, setPostingChannels] = useState<ChannelsValue>({
      publishToTalent: true,
      channels: { google_jobs: { enabled: true } },
    })

    /* --- apply experience --- */
    const [sendConfirm, setSendConfirm] = useState(true)
    const [promise48, setPromise48] = useState(true)
    const [allowMessage, setAllowMessage] = useState(false)
    const [enableReferral, setEnableReferral] = useState(true)

    /* --- SEO --- */
    const [metaTitle, setMetaTitle] = useState('')
    const [metaDescription, setMetaDescription] = useState('')
    useEffect(() => {
      if (!metaTitle && publicTitle) setMetaTitle(publicTitle.slice(0, 60))
    }, [publicTitle]) // eslint-disable-line react-hooks/exhaustive-deps

    /* --- meta back to wizard footer --- */
    useEffect(() => {
      onPostingMeta?.({ channels: enabledChannels.length, fields: fields.length })
    }, [enabledChannels.length, fields.length, onPostingMeta])

    /* --- save --- */
    React.useImperativeHandle(ref, () => ({
      async savePosting(opts) {
        if (!jobId) return { ok: true, postingId: null } // nothing to wire to — skip silently
        if (!publicTitle.trim()) {
          toast.error('Public job title is required')
          return { ok: false }
        }

        // Resolve department NAME for the public careers page. The wizard's
        // jobData carries department_id; postings need the human label too so
        // the careers page can group jobs without an extra join.
        const deptId: string | null = (jobData as any)?.department_id ?? null
        let deptName: string | null = (jobData as any)?.department ?? null
        if (deptId && !deptName) {
          const { data: deptRow } = await supabase
            .from('departments')
            .select('name')
            .eq('id', deptId)
            .maybeSingle()
          if (deptRow?.name) deptName = deptRow.name
        }

        const details = {
          slug, reference_id: refId || null, language,
          deadline: deadline ? deadline.toISOString().slice(0, 10) : null,
          show_in_search: showInSearch, show_response_badge: showResponseBadge,
          ...brandingDetails({ brandColor, bannerUrl, bannerName: bannerName || null, video }),
          application_fields: fields.map((f) => ({ id: f.id, label: f.label, type: f.type, required: f.required, locked: !!f.locked, hint: f.hint, isSmart: !!f.isSmart || SMART_FIELD_TYPES.has(f.type), ...(f.options ? { options: f.options } : {}) })),
          eeo_survey: eeo,
          channels: Object.entries(channelOn).filter(([, v]) => v).map(([id]) => id),
          apply_experience: { send_confirm: sendConfirm, promise_48h: promise48, allow_message: allowMessage, enable_referral: enableReferral },
          seo: { meta_title: metaTitle, meta_description: metaDescription },
          // Departments are the dividing category on the public careers page.
          // Denormalize from the parent job so postings stay groupable without an extra join.
          department: deptName,
          department_id: deptId,
          location: primaryLocation.trim() || null,
          additional_locations: additionalLocations,
          location_type: jobData.work_mode || null,
          employment_type: jobData.employment_type || null,
          salary_period: salaryPeriod,
          compensation: {
            variable_enabled: variableEnabled,
            commission_currency: variableEnabled ? commissionCurrency : null,
            commission_amount: variableEnabled ? commissionAmount ?? null : null,
          },
        }

        try {
          // Create the posting as DRAFT (is_active = false). The wizard's
          // Summary step "Publish to careers page immediately" toggle is what
          // flips it live on Create — that happens in JobWizard.handleComplete.
          // A slug someone typed is saved as is; untouched, one is made from the title.
          const chosenSlug = slugTouched.current ? finishSlug(slug) : ''
          const created = await createPosting({
            title: publicTitle,
            slug: chosenSlug || undefined,
            onSlugTaken: () => {
              setSlugError('That URL is taken. Try another.')
              shakeFirstInvalid()
            },
            description,
            details,
            is_active: opts?.publish === true ? true : false,
            publish_to_talent: postingChannels.publishToTalent,
          })
          if (!created) return { ok: false }

          // Persist custom (non-locked, non-core) application questions into
          // job_posting_application_fields — the table that PostingSheet and
          // the public application form actually read from. The JSON snapshot
          // above is kept for backward compatibility (previews, summaries).
          const customFields = fields.filter((f) => !f.locked)
          if (customFields.length > 0) {
            const rows = customFields.map((f, index) => ({
              posting_id: created.id,
              source: 'custom' as const,
              application_field_id: null,
              field_name: sanitizeFieldName(f.label, index),
              field_label: f.label,
              field_type: sharedTypeToDbType(f.type),
              is_required: f.required,
              column_span: 4,
              placeholder_text: null,
              help_text: f.hint || null,
              accepted_file_types: null,
              max_file_size_mb: null,
              field_config: (f as any).fieldConfig ?? null,
              display_order: index,
            }))
            const { data: savedFields, error: fieldsErr } = await supabase
              .from('job_posting_application_fields')
              .insert(rows as any)
              .select('id, field_name')
            // Options for single-select and multiple-choice questions.
            if (!fieldsErr && savedFields) {
              const optionRows = customFields.flatMap((f, index) => {
                const saved = savedFields.find((r: any) => r.field_name === rows[index].field_name)
                const opts = (f.options ?? []).filter((o) => o.label.trim())
                if (!saved || !opts.length || (f.type !== 'select' && f.type !== 'multiselect')) return []
                return opts.map((o, i) => ({ posting_field_id: saved.id, option_value: o.value, option_label: o.label.trim(), display_order: i }))
              })
              if (optionRows.length) {
                const { error: optErr } = await supabase.from('posting_field_select_options').insert(optionRows as any)
                if (optErr) {
                  console.error('Error saving question options:', optErr)
                  toast.error('Posting created, but some question options failed to save — open the posting to add them.')
                }
              }
            }
            if (fieldsErr) {
              console.error('Error saving custom application fields:', fieldsErr)
              toast.error('Posting created, but custom questions failed to save — open the posting to retry.')
              // Don't block advance; user can fix from PostingSheet.
            }
          }

          return { ok: true, postingId: created.id }
        } catch (e: any) {
          toast.error(e?.message || 'Failed to create posting')
          return { ok: false }
        }
      },
    }))


    return (
      <div className="space-y-6 pb-6">
        {/* ---------- POSTING BASICS ---------- */}
        <SectionCard
          title="Posting basics"
          trailing={<AiAssistedBadge>Pulled from step 1</AiAssistedBadge>}
        >
          <div>
            <FieldLabel required>Public job title</FieldLabel>
            <Input value={publicTitle} onChange={(e) => setPublicTitle(e.target.value)} className="mt-2 h-11" />
            <FieldHint>What candidates see at the top of the posting. Defaults to your internal title — tune for search.</FieldHint>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <FieldLabel required>URL slug</FieldLabel>
              <AffixInput
                wrapperClassName="mt-2 rounded-xl bg-white"
                prefix="/jobs/"
                value={slug}
                invalid={!!slugError}
                onChange={(e) => {
                  slugTouched.current = true
                  setSlug(typedSlug(e.target.value))
                  setSlugError(null)
                }}
                onBlur={() => setSlug((s) => finishSlug(s))}
              />
              {slugError
                ? <FieldHint tone="error">{slugError}</FieldHint>
                : <FieldHint>your-careers-domain/jobs/{slug || '…'}</FieldHint>}
            </div>
            <div>
              <FieldLabel optional>Reference ID</FieldLabel>
              <Input value={refId} onChange={(e) => setRefId(e.target.value)} className="mt-2 h-11" placeholder="e.g., DES-2026-014" />
              <FieldHint>Shown in confirmation emails.</FieldHint>
            </div>
            <div>
              <FieldLabel required>Posting language</FieldLabel>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGUAGE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel optional>Application deadline</FieldLabel>
              <div className="mt-2">
                <DatePickerVirgilio
                  value={deadline}
                  onChange={setDeadline}
                  placeholder="Pick a date"
                  minDate={new Date()}
                  className="h-11 w-full"
                />
              </div>
              <FieldHint>Leave empty for rolling.</FieldHint>
            </div>

          </div>
          <div className="border-t border-virgilio-border pt-4 space-y-1">
            <ToggleRow
              label="Show in public job search"
              hint="When off, candidates can only apply via a direct link."
              checked={showInSearch}
              onChange={setShowInSearch}
            />
            <ToggleRow
              label="Show 'apply within 24h response' badge"
              hint="We've measured your team's avg first response at 18h — eligible."
              checked={showResponseBadge}
              onChange={setShowResponseBadge}
            />
          </div>
        </SectionCard>

        {/* ---------- LOCATIONS ---------- */}
        <SectionCard title="Locations" trailing={<AiAssistedBadge>Pulled from step 1</AiAssistedBadge>}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <FieldLabel htmlFor="posting-primary-location">Primary location</FieldLabel>
              <div className="relative mt-2">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary pointer-events-none" />
                <Input
                  id="posting-primary-location"
                  value={primaryLocation}
                  onChange={(e) => setPrimaryLocation(e.target.value)}
                  placeholder="e.g. New York, NY"
                  className="pl-9 h-11"
                />
              </div>
              <FieldHint>The main location shown on this job post.</FieldHint>
            </div>
            <div>
              <FieldLabel optional>Additional locations</FieldLabel>
              <div className="mt-2">
                <ChipInput
                  values={additionalLocations}
                  onChange={setAdditionalLocations}
                  placeholder="Add location…"
                  tone="purple"
                />
              </div>
              <FieldHint>Press Enter or comma after each location.</FieldHint>
            </div>
          </div>
        </SectionCard>

        {/* ---------- COMPENSATION ---------- */}
        <SectionCard title="Compensation">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
            <div>
              <FieldLabel required>Currency</FieldLabel>
              <div className="mt-2">
                <CurrencySelect
                  value={jobData.currency || 'USD'}
                  onChange={(v) => setJob('currency', v)}
                />
              </div>
            </div>
            <div>
              <FieldLabel required>Min salary</FieldLabel>
              <div className="mt-2">
                <SalaryInput
                  value={jobData.salary_min ?? undefined}
                  symbol={currencySymbol(jobData.currency)}
                  suffix={periodSuffix(salaryPeriod)}
                  onChange={(v) => setJob('salary_min', v)}
                  placeholder="80,000"
                  invalid={salaryInvalid}
                />
              </div>
              {salaryInvalid && <FieldHint tone="error">Min must be lower than max</FieldHint>}
            </div>
            <div>
              <FieldLabel required>Max salary</FieldLabel>
              <div className="mt-2">
                <SalaryInput
                  value={jobData.salary_max ?? undefined}
                  symbol={currencySymbol(jobData.currency)}
                  suffix={periodSuffix(salaryPeriod)}
                  onChange={(v) => setJob('salary_max', v)}
                  placeholder="120,000"
                />
              </div>
            </div>
            <div>
              <FieldLabel>Pay period</FieldLabel>
              <div className="mt-2">
                <Select value={salaryPeriod} onValueChange={setSalaryPeriod}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annually">Per year</SelectItem>
                    <SelectItem value="monthly">Per month</SelectItem>
                    <SelectItem value="hourly">Per hour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="border-t border-virgilio-border pt-4 space-y-1">
            <ToggleRow
              label="Show salary on public posting"
              hint="Recommended — applicant quality jumps 40% on jobs that publish salary."
              checked={!!jobData.show_salary_public}
              onChange={(v) => setJob('show_salary_public', v)}
            />
            <ToggleRow
              label="Include equity"
              checked={!!jobData.include_equity}
              onChange={(v) => setJob('include_equity', v)}
            />
            <ToggleRow
              label="Include signing bonus"
              checked={!!jobData.include_signing_bonus}
              onChange={(v) => setJob('include_signing_bonus', v)}
            />
            <ToggleRow
              label="Include variable / commission"
              hint="On-target earnings, sales commission, or bonus structure."
              checked={variableEnabled}
              onChange={setVariableEnabled}
            />
          </div>

          {variableEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
              <div>
                <FieldLabel>Commission currency</FieldLabel>
                <div className="mt-2">
                  <CurrencySelect value={commissionCurrency} onChange={setCommissionCurrency} />
                </div>
              </div>
              <div className="sm:col-span-2">
                <FieldLabel>Commission / variable amount</FieldLabel>
                <div className="mt-2">
                  <SalaryInput
                    value={commissionAmount}
                    onChange={setCommissionAmount}
                    placeholder="e.g. 20,000 OTE"
                  />
                </div>
                <FieldHint>On-target earnings or % — your call.</FieldHint>
              </div>
            </div>
          )}
        </SectionCard>


        {/* ---------- PUBLIC DESCRIPTION ---------- */}
        <SectionCard
          title="Public description"
          trailing={
            <div className="flex items-center gap-2">
              {initedDesc.current && <AiAssistedBadge>Gio rewrote</AiAssistedBadge>}
              <button
                type="button"
                onClick={handleGenerate}
                disabled={genLoading}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-poppins font-medium text-text-secondary hover:text-text-primary"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {genLoading ? 'Rewriting…' : 'Rewrite'}
              </button>
            </div>
          }
        >
          <div>
            <FieldLabel required>Posting copy</FieldLabel>
            <div className="mt-2">
              <FormattedTextBox
                value={description}
                onChange={setDescription}
                headings
                ariaLabel="Posting copy"
                placeholder="About the role…"
                minHeight={260}
              />
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <FieldHint>We've optimized your internal description for tone, inclusivity, and SEO.</FieldHint>
              <Button variant="purple" size="sm" icon={Sparkles} onClick={handleGenerate} loading={genLoading}>
                Generate with Gio
              </Button>
            </div>
          </div>
          <div className="rounded-xl bg-[#F2EBFF] p-4 flex items-start gap-3">
            <div className="h-9 w-9 shrink-0 rounded-lg bg-virgilio-purple flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-poppins font-medium text-text-primary">Inclusion score · 92/100</p>
              <p className="text-[12px] text-text-secondary mt-0.5">Strong. We scan for gendered terms and overly strict requirements.</p>
            </div>
          </div>
        </SectionCard>

        {/* ---------- BRANDING ---------- */}
        <SectionCard
          title="Branding"
          trailing={<span className="text-[11px] font-poppins font-medium text-text-tertiary uppercase tracking-[0.08em]">Inherits from workspace</span>}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <FieldLabel optional>Hero banner</FieldLabel>
              <label
                className="mt-2 block rounded-xl border border-dashed border-virgilio-border bg-[#FAFAF7] aspect-[16/5] cursor-pointer hover:bg-[#F2EBFF] transition-colors relative overflow-hidden"
                style={
                  bannerUrl
                    ? { backgroundImage: `url(${bannerUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                    : { background: `linear-gradient(135deg, ${brandColor}, ${brandColor}30)` }
                }
              >
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  disabled={bannerUploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null
                    void handleBannerFileChange(f)
                    e.currentTarget.value = ''
                  }}
                />
                <span className="absolute bottom-3 left-3 text-[11.5px] text-white font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                  {bannerUploading
                    ? 'Uploading…'
                    : bannerUrl
                    ? (bannerName || 'Banner uploaded — click to replace')
                    : 'Click to upload (1600 × 480 recommended · PNG/JPG/WebP · max 5 MB)'}
                </span>
                {bannerUrl && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setBannerUrl(null)
                      setBannerName('')
                    }}
                    className="absolute top-2 right-2 h-7 px-2 rounded-md bg-black/60 text-white text-[11px] font-poppins font-medium hover:bg-black/75"
                  >
                    Remove
                  </button>
                )}
              </label>
              <FieldHint>Shown at the top of the public job posting. Falls back to the brand-color gradient.</FieldHint>
            </div>
            <div>
              <FieldLabel>Brand color</FieldLabel>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="color"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="h-11 w-12 rounded-lg border border-virgilio-border cursor-pointer bg-white"
                />
                <Input value={brandColor} onChange={(e) => setBrandColor(e.target.value)} className="h-11 flex-1 font-mono uppercase" />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[11.5px] text-text-tertiary">Swatches</span>
                {BRAND_SWATCHES.map((c) => (
                  <button key={c} type="button" onClick={() => setBrandColor(c)}
                    aria-label={`Use ${c}`}
                    className={cn('h-6 w-6 rounded-md border', brandColor.toLowerCase() === c.toLowerCase() ? 'ring-2 ring-virgilio-purple ring-offset-1' : 'border-virgilio-border')}
                    style={{ background: c }} />
                ))}
              </div>
              <FieldHint>Used for buttons and accents on the posting.</FieldHint>
            </div>
          </div>
          <div className="border-t border-virgilio-border pt-4 space-y-1">
            <PostingVideoField value={video} onChange={setVideo} jobId={jobId} />
          </div>
        </SectionCard>

        {/* ---------- APPLICATION FORM ---------- */}
        <ApplicationFormBuilder
          fields={fields}
          onChange={setFields}
          eeoEnabled={eeo}
          onEeoChange={setEeo}
          extraTrailing={
            <CopyFromAnotherJobButton
              excludeJobId={jobId}
              currentFieldCount={fields.length}
              onCopy={(jobTitle, copied) => {
                setFields(copied)
                toast.success(`Copied ${copied.length} fields from ${jobTitle}`)
              }}
            />
          }
        />

        {/* ---------- WHERE TO PUBLISH ---------- */}
        <SectionCard
          title="Where to publish"
          trailing={
            <a href="/settings?tab=integrations" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-poppins font-medium text-text-secondary hover:text-text-primary">
              <ExternalLink className="h-3.5 w-3.5" />
              Manage integrations
            </a>
          }
        >
          <div className="space-y-2">
            {CHANNELS.map((c) => (
              <div key={c.id} className="flex items-center gap-3 rounded-xl border border-virgilio-border bg-white px-3 py-3">
                <div className={cn('h-10 w-10 shrink-0 rounded-lg inline-flex items-center justify-center font-poppins font-semibold text-[13px]', c.letterBg)}>
                  {c.letter}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[13px] font-poppins font-medium text-text-primary truncate">{c.id === 'careers' ? careersPageName : c.name}</p>
                    {c.id === 'careers' && <span className="text-[10.5px] uppercase tracking-[0.08em] font-poppins font-semibold text-virgilio-purple bg-[#EDE4FF] rounded-full px-2 py-0.5">Recommended</span>}
                  </div>
                  <p className="text-[11.5px] text-text-tertiary truncate">{c.sub}</p>
                </div>
                <span className="text-[11.5px] text-text-tertiary text-right whitespace-nowrap mr-1">{c.status}</span>
                <ToggleRow
                  label=""
                  checked={!!channelOn[c.id]}
                  onChange={(v) => !c.alwaysOn && setChannelOn((m) => ({ ...m, [c.id]: v }))}
                  disabled={c.alwaysOn}
                />
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-[#0d0d09] text-white p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-white/10 inline-flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-poppins font-medium">Posting total · ${totalCost} {totalCost > 0 && '+ 1 sourcing credit'}</p>
              <p className="text-[12px] text-white/60">Charged on publish. Cancel or pause campaigns from the posting page anytime.</p>
            </div>
            <span className="text-[11.5px] bg-white/10 rounded-full px-3 py-1">{freeCount} free, {paidCount} paid</span>
          </div>
        </SectionCard>

        {/* ---------- SYNDICATION CHANNELS (wired to DB) ---------- */}
        <SectionCard title="Syndication channels">
          <PostingChannelsCard value={postingChannels} onChange={setPostingChannels} />
        </SectionCard>



        {/* ---------- APPLY EXPERIENCE ---------- */}
        <SectionCard title="Apply experience">
          <ToggleRow label="Send confirmation email" hint={`From noreply@app.gogio.io · 'We received your application — here's what's next'`} checked={sendConfirm} onChange={setSendConfirm} />
          <ToggleRow label="Promise first response in 48h" hint="Posts a visible badge on the listing." checked={promise48} onChange={setPromise48} />
          <ToggleRow label="Allow candidate to message recruiter" hint="Through the candidate portal." checked={allowMessage} onChange={setAllowMessage} />
          <ToggleRow
            label="Enable referral link"
            hint={`your-careers-domain/r/${slug || 'job'} · trackable per-member.`}
            checked={enableReferral}
            onChange={setEnableReferral}
          />
        </SectionCard>

        {/* ---------- SEO & SHARING ---------- */}
        <SectionCard
          title="SEO & sharing"
          trailing={<AiAssistedBadge>Gio generated</AiAssistedBadge>}
        >
          <div>
            <FieldLabel>Meta title</FieldLabel>
            <Input value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} maxLength={60} className="mt-2 h-11" />
            <CharCounterLine count={metaTitle.length} max={60} className="mt-1.5 text-text-tertiary">Shown in search engines.</CharCounterLine>
          </div>
          <div>
            <FieldLabel>Meta description</FieldLabel>
            <Textarea value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} maxLength={155} className="mt-2 min-h-[88px]" />
            <CharCounterLine count={metaDescription.length} max={155} className="mt-1.5 text-text-tertiary">Shown under the title in search results.</CharCounterLine>
          </div>
          <div>
            <p className="text-[10.5px] font-poppins font-semibold uppercase tracking-[0.12em] text-text-secondary mb-2">Social card preview</p>
            <div className="rounded-xl border border-virgilio-border overflow-hidden bg-white">
              <div className="aspect-[1200/520] relative" style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor}40)` }}>
                <span className="absolute top-4 left-4 text-[11.5px] bg-[#0d0d09] text-white rounded-full px-2.5 py-1 font-medium">Hiring</span>
              </div>
              <div className="p-4">
                <p className="text-[10.5px] uppercase tracking-[0.08em] text-text-tertiary">your-careers-domain</p>
                <p className="text-[14px] font-poppins font-semibold text-text-primary mt-1">{metaTitle || publicTitle || 'Job title'}</p>
                <p className="text-[12.5px] text-text-secondary mt-1 line-clamp-2">{metaDescription || 'A short description of this role.'}</p>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* ---------- PREVIEW TEASER ---------- */}
        <div className="rounded-2xl border border-virgilio-border bg-white p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-[#EDE4FF] inline-flex items-center justify-center">
            <Eye className="h-4 w-4 text-virgilio-purple" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-poppins font-medium text-text-primary">Preview the full posting</p>
            <p className="text-[12px] text-text-secondary">Open the candidate-facing posting in a new tab. Nothing is published until the next step.</p>
          </div>
          <Button variant="secondary" size="sm" iconRight={ExternalLink} disabled>
            Open preview
          </Button>
        </div>
      </div>
    )
  }
)

/* ---------------- Copy from another job ---------------- */

function fieldsFromPostingDetails(details: any): AppField[] {
  const raw = (details?.application_fields ?? details?.fields ?? []) as any[]
  return raw.map((f, i) => ({
    id: f.id ?? `copied_${i}_${Date.now()}`,
    label: f.label ?? 'Untitled',
    type: (f.type ?? 'text') as FieldType,
    hint: f.hint,
    required: !!f.required,
    locked: !!f.locked,
    isSmart: !!f.isSmart || SMART_FIELD_TYPES.has(f.type),
    icon: iconForType(f.type),
  }))
}

function CopyFromAnotherJobButton({
  excludeJobId,
  currentFieldCount,
  onCopy,
}: {
  excludeJobId: string | null
  currentFieldCount: number
  onCopy: (jobTitle: string, copied: AppField[]) => void
}) {
  const { jobs, isLoading } = useJobsWithPostings(excludeJobId)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return jobs
    return jobs.filter(
      (j) =>
        j.title.toLowerCase().includes(q) ||
        (j.department ?? '').toLowerCase().includes(q),
    )
  }, [jobs, query])

  const handleSelect = async (j: (typeof jobs)[number]) => {
    const copied = fieldsFromPostingDetails(j.posting_details)
    if (copied.length === 0) {
      toast.error('That job has no application fields to copy')
      return
    }
    if (currentFieldCount > 0) {
      const ok = await confirmDialog({
        title: `Replace the current ${currentFieldCount} application field${currentFieldCount === 1 ? '' : 's'}?`,
        description: `They'll be replaced with ${copied.length} from "${j.title}".`,
        confirmLabel: 'Replace',
      })
      if (!ok) return
    }
    onCopy(j.title, copied)
    setOpen(false)
    setQuery('')
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" icon={Copy} dropdown>
          Copy from another job
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-[320px]">
        <DropdownMenuLabel>Copy application form</DropdownMenuLabel>
        {jobs.length >= 7 && (
          <div className="px-2 pt-1 pb-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-tertiary" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search jobs…"
                className="w-full h-8 rounded-md border border-virgilio-border bg-white pl-7 pr-2 text-[12.5px] outline-none focus:ring-2 focus:ring-virgilio-purple/30"
              />
            </div>
          </div>
        )}
        {isLoading ? (
          <div className="space-y-1.5 p-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-9 rounded-md bg-[#F1F0EC] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-[12.5px] text-text-tertiary">
            {jobs.length === 0
              ? 'No other jobs have a posting yet.'
              : 'No jobs match your search.'}
          </div>
        ) : (
          <div className="max-h-[280px] overflow-y-auto">
            {filtered.map((j) => (
              <DropdownMenuItem
                key={j.id}
                onSelect={(e) => {
                  e.preventDefault()
                  handleSelect(j)
                }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-poppins font-medium text-text-primary truncate">
                    {j.title}
                  </p>
                  {j.department && (
                    <p className="text-[11px] text-text-tertiary truncate">{j.department}</p>
                  )}
                </div>
                <Badge tone="neutral" size="xs">
                  {j.field_count} {j.field_count === 1 ? 'field' : 'fields'}
                </Badge>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
